import { useQuery } from '@tanstack/react-query';
import { IEntity } from '@opengov/og-api-ts';
import { getCurrentOrLegacySavedViews, ICurrentOrLegacySavedView } from '@opengov/reporting-legacy-module';

import { useToast } from '../../../../stories/components/Toast/context/useToast.context';
import { embedPickerQueryKeys, SavedViewsKeyParams } from './embedPickerQueryKeys';

// Key params drive cache identity; entity is session-stable and excluded from the key.
export type UseSavedViewsForReportParams = (SavedViewsKeyParams & { entity: IEntity }) | null;

export function useSavedViewsForReport(params: UseSavedViewsForReportParams) {
  const { toggleToast } = useToast();

  const keyParams: SavedViewsKeyParams | null = params
    ? { reportId: params.reportId, legacyReportId: params.legacyReportId, reportVersion: params.reportVersion }
    : null;

  return useQuery<ICurrentOrLegacySavedView[], Error>({
    queryKey: keyParams
      ? embedPickerQueryKeys.savedViews(keyParams)
      : ([...embedPickerQueryKeys.all, 'savedViews', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const result = await getCurrentOrLegacySavedViews({
        reportId: params.reportId,
        legacyReportId: params.legacyReportId,
        reportVersion: params.reportVersion,
        // IEntity from og-api-ts and api-deprecated (the SDK's IEntity) are
        // structurally identical; bridge the two package types.
        entity: params.entity as unknown as Parameters<typeof getCurrentOrLegacySavedViews>[0]['entity']
      });
      return result ?? [];
    },
    enabled: params !== null,
    staleTime: 5 * 60 * 1000,
    onError: () => {
      toggleToast({ title: 'Could not load saved views', severity: 'error' });
    }
  });
}
